import jsPDF from "jspdf";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import "jspdf-autotable";
import { parseTaxValue } from "./utils";
import { categorizeTax } from "../../../../utills/tax-categorizer";

/** Gera número único de cotação */
export const generateQuotationNo = (record: any): string => {
  const containerSize = record.container_size || "";
  const etd = record.etd ? dayjs(record.etd).format("YYYY-MM-DD") : "";
  const transitDays = parseInt((record.transit_time || "0").replace(/\D/g, "")) || 0;
  const eta = etd ? dayjs(etd).add(transitDays, "days").format("YYYY-MM-DD") : "";
  const from = record.from || "";
  const fromCode = record.from_code || (from.split(" ")[0] || "");

  const merged = `${containerSize}-${eta}-${etd}-${from}-${fromCode}`.toUpperCase();
  let hash = 0;
  for (let i = 0; i < merged.length; i++) {
    hash = ((hash << 5) - hash) + merged.charCodeAt(i);
    hash |= 0;
  }

  const hashString = Math.abs(hash).toString(36).toUpperCase().padStart(6, "0");
  return `WLI-${hashString}`;
};

/** Soma total de base + bunker + isps (opcional) */
export const calculateTotal = (item: any): number => {
  if (!item) return 0;
  const parse = (value: any): number => {
    if (!value) return 0;
    if (typeof value === "number") return value;
    return parseFloat(String(value).replace(/[^\d.]/g, "")) || 0;
  };
  return parse(item.base_freight) + parse(item.bunker) + parse(item.isps);
};

/** Formata valor para USD */
export const formatCurrency = (value: string | number): string => {
  try {
    if (typeof value === "number") value = value.toString();
    const cleanValue = value.replace(/USD/g, "").trim();
    const numbersOnly = cleanValue.replace(/[^\d.]/g, "");
    if (!numbersOnly) return "N/A";
    if (numbersOnly.includes(".")) {
      const number = Math.round(parseFloat(numbersOnly) * 1000);
      return `${number.toLocaleString("en-US")} USD`;
    }
    const number = parseInt(numbersOnly, 10);
    return `${number.toLocaleString("en-US")} USD`;
  } catch {
    return "N/A";
  }
};

/** Data de hoje em DD/MM/YYYY */
export const getCurrentDate = (): string => {
  return dayjs().format("DD/MM/YYYY");
};



/** Soma de taxas por categoria */
const sumTaxes = (taxes, record) => {
  const INVALID = Symbol('invalid');
  const UNKNOWN_GROUP = 'unknown';

  // Normaliza a entrada
  const list = Array.isArray(taxes) ? taxes : [];
  const skipped = []; // opcional: para debug

  const safeParseValue = (v) => {
    try {
      const n = parseTaxValue ? parseTaxValue(v) : Number(v);
      return Number.isFinite(n) ? n : INVALID;
    } catch {
      return INVALID;
    }
  };

  const safeCategory = (tax) => {
    try {
      const cat = categorizeTax
        ? categorizeTax(tax?.category, tax?.currency, record, tax)
        : tax?.category;
      return cat || UNKNOWN_GROUP;
    } catch {
      return UNKNOWN_GROUP;
    }
  };

  const groupedTaxes = list.reduce((acc, tax, idx) => {
    // pula itens claramente inválidos
    if (!tax || typeof tax !== 'object') {
      skipped.push({ idx, reason: 'tax undefined/null ou não-objeto' });
      return acc;
    }

    // valida valor
    const valueNum = safeParseValue(tax.value);
    if (valueNum === INVALID) {
      skipped.push({ idx, reason: 'valor inválido', value: tax.value });
      return acc;
    }

    // resolve categoria de forma segura
    const category = safeCategory(tax);

    if (!acc[category]) acc[category] = [];
    // opcional: já guarda o valor numérico normalizado para evitar reparse
    acc[category].push({ ...tax, _valueNum: valueNum });
    return acc;
  }, {});

  // soma segura usando o valor já normalizado (_valueNum) como preferência
  const sumGroup = (group) =>
    (group || []).reduce((sum, tax) => {
      const v =
        typeof tax?._valueNum === 'number'
          ? tax._valueNum
          : safeParseValue(tax?.value);
      return v === INVALID ? sum : sum + v;
    }, 0);

  const res = {
    origin: sumGroup(groupedTaxes['Taxas locais da origem']),
    freigth: sumGroup(groupedTaxes['Frete e taxas que acompanham frete']), // mantém sua chave
    destination: sumGroup(groupedTaxes['Taxas locais do destino']),
  };

  // opcional: se quiser ver o que foi ignorado
  // console.debug('Taxes ignorados:', skipped);

  return res;
};

export const exportToExcel = (filteredData: any[]): void => {
  try {
    const currentDate = getCurrentDate();

    const sortedData = [...filteredData].sort(
      (a, b) => sumTaxes(a.values || [], a).freigth - sumTaxes(b.values || [], b).freigth
    );
    const exportData = sortedData.map((item) => {
      const quotationNo = generateQuotationNo(item);
      const taxes = sumTaxes(item.values || [], item);

      return {
        "Cotação Nº": quotationNo,
        "Armador": item.type?.toUpperCase() || "",
        "Origem": `${item.from_code || ""} ${item.from || ""}`.trim(),
        "Destino": `${item.to_code || ""} ${item.to || ""}`.trim(),
        "Tamanho do Container": item.container_size ? `${item.container_size}'` : "",
        "Valor Total": formatCurrency(taxes.freigth),
        "ETD": item.etd ? dayjs(item.etd).format("DD/MM/YYYY") : "",
        "ETA": item.eta ? dayjs(item.eta).format("DD/MM/YYYY") : "",
        "Tempo de Trânsito (T/T)": item.transit_time?.replace(/\D/g, "") + " dias" || "",
        "Validade": item.sailing_info?.offerExpire
          ? dayjs(item.sailing_info.offerExpire).format("DD/MM/YYYY")
          : "N/A",
        "Data da Cotação": currentDate,
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Fretes");
    ws["!cols"] = new Array(Object.keys(exportData[0]).length).fill({ wch: 20 });

    XLSX.writeFile(wb, `lista-fretes-${dayjs().format("DDMMYYYY")}.xlsx`);
  } catch (error) {
    console.error("Erro na exportação Excel:", error);
  }
};


export const exportToPDF = (filteredData: any[]): void => {
  try {
    const currentDate = getCurrentDate();
    const doc = new jsPDF("l", "mm", "a4");

    doc.setFontSize(16);
    doc.text("Lista de Fretes", 14, 15);
    doc.setFontSize(10);
    doc.text(`Data da Cotação: ${currentDate}`, 14, 22);
    doc.text(`Exportado em: ${dayjs().format("DD/MM/YYYY HH:mm")}`, 14, 28);

    const sortedData = [...filteredData].sort(
      (a, b) => sumTaxes(a.values || [], a).freigth - sumTaxes(b.values || [], b).freigth
    );

    const firstItem = sortedData[0];
    if (firstItem) {
      const origem = `${firstItem.from_code || ""} ${firstItem.from || ""}`.trim();
      const destino = `${firstItem.to_code || ""} ${firstItem.to || ""}`.trim();

      doc.setFontSize(11);
      doc.text(`Origem: ${origem}`, 14, 35);
      doc.text(`Destino: ${destino}`, 90, 35);
    }

    const exportData = sortedData.map((item) => {
      const quotationNo = generateQuotationNo(item);
      const taxes = sumTaxes(item.values || [], item);
      const vesselName = item.sailing_info?.vesselName || "N/A";

      return [
        quotationNo,
        item.type?.toUpperCase() || "",
        vesselName,
        item.container_size ? `${item.container_size}'` : "",
        formatCurrency(taxes.freigth),
        item.etd ? dayjs(item.etd).format("DD/MM/YYYY") : "",
        item.eta ? dayjs(item.eta).format("DD/MM/YYYY") : "",
        item.transit_time?.replace(/\D/g, "") + " dias" || "",
      ];
    });

    (doc as any).autoTable({
      head: [[
        "Cotação Nº", "Armador", "Navio", "Tamanho",
        "Valor Total", "ETD", "ETA", "T/T"
      ]],
      body: exportData,
      startY: 42,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: {
        fillColor: [51, 122, 183],
        textColor: 255,
        fontSize: 8,
        fontStyle: "bold",
      },
    });

    doc.save(`lista-fretes-${dayjs().format("DDMMYYYY")}.pdf`);
  } catch (error) {
    console.error("Erro na exportação PDF:", error);
  }
};
